/**
 * Camadas da qualidade do ar no mapa.
 *
 * - **Pontos** (`Z.AR`): sensores de baixo custo de todas as redes (hexágono)
 *   e estações do MonitorAr (losango), coloridos pela faixa do valor NO QUADRO
 *   exibido, na escala da rede de origem — é isso que faz o ar mudar junto com a animação. ~100–250
 *   pontos: a camada vetorial comum dá conta, e o estilo nativo entra na
 *   exportação.
 * - **Modelo** (`Z.AR_MODELO`): a grade do CAMS (~720 células de 0,8°),
 *   preenchida pela faixa do PM2,5 do modelo (CONAMA 491) na hora cheia que contém o quadro,
 *   translúcida (a opacidade é da camada, e não da tinta: assim as bordas de
 *   células vizinhas não se somam). Fica logo acima da fumaça e abaixo de
 *   todo vetor. Não responde ao clique como feature — a ficha da célula é um
 *   popup por coordenada, consultado só quando nada por cima respondeu (um
 *   foco, um ponto medido, a fumaça).
 *
 * - **Destaque** (`Z.AR_MODELO_DESTAQUE`): o contorno da célula cuja ficha
 *   está aberta — a área a que os números da ficha se referem.
 *
 * Os estilos ficam em cache por aparência; um quadro novo só redesenha quando
 * alguma cor de fato muda.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import RegularShape from 'ol/style/RegularShape';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { registerCoordinatePopup } from '../map/popupControl';
import { lonLatToMap, mapToLonLat } from '../map/projection';
import { Z } from '../map/zIndex';
import { HOUR_MS, MINUTE_MS } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { atribuicaoAr, CAMS_ATRIBUICAO_MAPA, celulaEm, indiceDeCelulas } from './api';
import { corDaFaixa, faixaNaEscala, faixaPm25, type Faixa } from './faixas';
import { chaveEstilo, visualDe, type Visual } from './look';
import { escalaDoSensor } from './sensor';
import { useArStore } from './store';
import { indiceQueContem, parado, ultimoAte, VALIDADE_MAPA_MS } from './tempo';
import type { Cams, Estacao, IqarHora, Sensor } from './types';

/** O que a ficha recebe ao clicar num ponto. */
export interface PontoFeature {
  ponto: Sensor | Estacao;
  /** Faixa no quadro exibido; `null` = sem dado neste instante. */
  faixa: Faixa | null;
}

const estilos = criarCache<Style[]>('ar.estilos', 600);

function estiloDe(v: Visual): Style[] {
  const k = chaveEstilo(v, dpr());
  const pronto = estilos.get(k);
  if (pronto) return pronto;
  const lados = v.forma === 'hexagono' ? 6 : 4;
  const forma = new RegularShape({
    points: lados,
    radius: v.raio,
    angle: 0,
    fill: v.preenchimento ? new Fill({ color: v.preenchimento }) : undefined,
    stroke: new Stroke({
      color: v.contorno,
      width: v.larguraContorno,
      lineDash: v.tracejado.length ? [...v.tracejado] : undefined,
      lineJoin: 'round',
    }),
  });
  forma.setOpacity(v.opacidade);
  const saida = [new Style({ image: forma, zIndex: 1 })];
  if (v.anel) {
    // Anel claro por fora do losango: a marca de "estação oficial".
    const anel = new RegularShape({
      points: lados,
      radius: v.raio + 2.6,
      angle: 0,
      stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 1.4 }),
    });
    anel.setOpacity(v.opacidade);
    saida.unshift(new Style({ image: anel, zIndex: 0 }));
  }
  estilos.set(k, saida);
  return saida;
}

/** Aparência de um ponto na resolução do mapa: o símbolo cresce com o zoom pela regra comum (`map/escalaDoZoom.ts`). */
export function visualNoZoom(
  tipo: 'sensor' | 'estacao',
  faixa: Faixa | null,
  estaParado: boolean,
  estilo: { tamanho: number; opacidade: number },
  resolution: number | undefined,
): Visual {
  return visualDe(tipo, faixa, estaParado, estilo.tamanho * fatorDoZoom(resolution), estilo.opacidade);
}

/**
 * A faixa de um ponto no instante: a leitura mais recente até ele, de no
 * máximo 24 h antes (regra dos pluviômetros; ver tempo.ts), na escala da rede
 * do ponto. Nunca do futuro.
 */
export function faixaNoQuadro(
  p: Sensor | Estacao,
  pontos: { t0: number; passoMin: number; h0: number },
  instante: number | null,
): Faixa | null {
  if (p.tipo === 'sensor') {
    const a = ultimoAte(p.serie, pontos.t0, pontos.passoMin * MINUTE_MS, instante, VALIDADE_MAPA_MS);
    return a ? faixaNaEscala(escalaDoSensor(p.fonte), a.valor) : null;
  }
  const a = ultimoAte<IqarHora>(p.horas, pontos.h0, HOUR_MS, instante, VALIDADE_MAPA_MS);
  return a ? a.valor.faixa : null;
}

function ultimaDe(p: Sensor | Estacao): number | null {
  return p.tipo === 'sensor' ? p.ultima.t : p.ultima;
}

export interface ArLayers {
  pontos: VectorLayer<VectorSource>;
  modelo: VectorLayer<VectorSource>;
  destaque: VectorLayer<VectorSource>;
  dispose: () => void;
}

export function createArLayers(
  renderPonto: (f: PontoFeature) => ReactNode | null,
  renderCelula: (indice: number) => ReactNode | null,
): ArLayers {
  // ---------------------------------------------------------------- pontos
  const fontePontos = new VectorSource({ attributions: atribuicaoAr(useArStore.getState().pontos) });
  const pontos = new VectorLayer({
    source: fontePontos,
    zIndex: Z.AR,
    className: 'ol-layer amaview-ar',
    properties: { id: 'ar', title: 'Qualidade do ar (medições)' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
    // Estação oficial por cima do sensor quando os dois caem no mesmo lugar.
    renderOrder: (a, b) => ((a.get('ordem') as number) ?? 0) - ((b.get('ordem') as number) ?? 0),
  });
  pontos.setStyle((feature, resolution) => {
    const d = feature.get('ar') as PontoFeature | undefined;
    if (!d) return [];
    const { estilo } = useArStore.getState();
    const estaParado = parado(ultimaDe(d.ponto));
    if (estaParado && !estilo.mostrarParados) return [];
    const v = visualNoZoom(d.ponto.tipo, d.faixa, estaParado, estilo, resolution);
    return comSelo(estiloDe(v), v.raio + (v.anel ? 4 : 2), resolution);
  });
  pontos.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('ar') as PontoFeature | undefined;
    return d ? renderPonto(d) : null;
  });

  function reconstruirPontos(): void {
    const { pontos: dados } = useArStore.getState();
    // A atribuição acompanha as redes no mapa ("Data from PurpleAir" quando há aparelho dela).
    fontePontos.setAttributions(atribuicaoAr(dados));
    fontePontos.clear(true);
    const todos: (Sensor | Estacao)[] = [...dados.estacoes, ...dados.sensores];
    fontePontos.addFeatures(
      todos.map((p) => {
        const f = new Feature({ geometry: new Point([p.mx, p.my]) });
        f.set('ar', { ponto: p, faixa: null } satisfies PontoFeature);
        f.set('ordem', p.tipo === 'estacao' ? 1 : 0);
        return f;
      }),
    );
    reavaliarPontos(true);
  }

  function reavaliarPontos(forcar = false): void {
    const { pontos: dados, visivel } = useArStore.getState();
    pontos.setVisible(visivel);
    if (!visivel) return;
    const instante = shownTimeOf(useTimeStore.getState());
    let mudou = forcar;
    for (const f of fontePontos.getFeatures()) {
      const d = f.get('ar') as PontoFeature;
      const faixa = faixaNoQuadro(d.ponto, dados, instante);
      if (faixa === d.faixa) continue;
      f.set('ar', { ponto: d.ponto, faixa } satisfies PontoFeature, true);
      mudou = true;
    }
    if (mudou) pontos.changed();
  }

  // ---------------------------------------------------------------- modelo
  const fonteModelo = new VectorSource({ attributions: CAMS_ATRIBUICAO_MAPA });
  const modelo = new VectorLayer({
    source: fonteModelo,
    zIndex: Z.AR_MODELO,
    className: 'ol-layer amaview-ar-modelo',
    properties: { id: 'ar-modelo', title: 'Qualidade do ar (modelo CAMS)' },
    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });
  const estilosCelula = new Map<Faixa, Style>();
  let horaAtual = -1;
  let camsDesenhado: Cams | null = null;
  let indiceCelulas = new Map<string, number>();

  modelo.setStyle((feature) => {
    const cams = camsDesenhado;
    const i = feature.get('celula') as number | undefined;
    if (!cams || i === undefined || horaAtual < 0) return [];
    const faixa = faixaPm25(cams.pm25[i]?.[horaAtual] ?? null);
    if (!faixa) return [];
    let s = estilosCelula.get(faixa);
    if (!s) {
      s = new Style({ fill: new Fill({ color: corDaFaixa(faixa) }) });
      estilosCelula.set(faixa, s);
    }
    return s;
  });

  /** Quatro cantos e os meios dos lados: na projeção do GOES as linhas de lon/lat já não são retas no extremo da Amazônia. */
  function anelDaCelula(c: { lon: number; lat: number }, m: number): number[][] {
    return [
      [c.lon - m, c.lat - m],
      [c.lon, c.lat - m],
      [c.lon + m, c.lat - m],
      [c.lon + m, c.lat],
      [c.lon + m, c.lat + m],
      [c.lon, c.lat + m],
      [c.lon - m, c.lat + m],
      [c.lon - m, c.lat],
      [c.lon - m, c.lat - m],
    ].map(([lon, lat]) => lonLatToMap(lon!, lat!));
  }

  function reconstruirModelo(): void {
    const { cams } = useArStore.getState();
    fonteModelo.clear(true);
    camsDesenhado = cams;
    indiceCelulas = cams ? indiceDeCelulas(cams) : new Map();
    if (cams) {
      fonteModelo.addFeatures(
        cams.celulas.map((c, i) => {
          const f = new Feature({ geometry: new Polygon([anelDaCelula(c, cams.passo / 2)]) });
          f.set('celula', i);
          return f;
        }),
      );
    }
    horaAtual = -1;
    reavaliarModelo();
    reavaliarDestaque();
  }

  // ---------------------------------------------------------------- destaque
  // O contorno da célula com a ficha aberta: um traço claro com um escuro por
  // baixo, legível sobre nuvem branca e sobre a noite.
  const fonteDestaque = new VectorSource();
  const destaque = new VectorLayer({
    source: fonteDestaque,
    zIndex: Z.AR_MODELO_DESTAQUE,
    className: 'ol-layer amaview-ar-destaque',
    properties: { id: 'ar-modelo-destaque', title: 'Qualidade do ar (célula do modelo selecionada)' },
    style: [
      new Style({ stroke: new Stroke({ color: 'rgba(8,13,24,0.85)', width: 4.5 }) }),
      new Style({ stroke: new Stroke({ color: '#ffffff', width: 2 }), fill: new Fill({ color: 'rgba(255,255,255,0.08)' }) }),
    ],
  });
  let celulaDesenhada: number | null = null;

  function reavaliarDestaque(): void {
    const { celulaAberta, visivel, estilo } = useArStore.getState();
    const cams = camsDesenhado;
    const i = visivel && estilo.modelo && cams && celulaAberta !== null && cams.celulas[celulaAberta] ? celulaAberta : null;
    if (i === celulaDesenhada && fonteDestaque.getFeatures().length === (i === null ? 0 : 1)) return;
    celulaDesenhada = i;
    fonteDestaque.clear(true);
    if (i !== null) fonteDestaque.addFeature(new Feature({ geometry: new Polygon([anelDaCelula(cams!.celulas[i]!, cams!.passo / 2)]) }));
    fonteDestaque.changed();
  }

  function reavaliarModelo(): void {
    const { visivel, estilo } = useArStore.getState();
    const ligado = visivel && estilo.modelo;
    modelo.setVisible(ligado);
    modelo.setOpacity(estilo.opacidadeModelo);
    if (!ligado || !camsDesenhado) return;
    const i = indiceQueContem(camsDesenhado.horas, camsDesenhado.h0, HOUR_MS, shownTimeOf(useTimeStore.getState()));
    if (i === horaAtual) return;
    horaAtual = i;
    modelo.changed();
  }

  const pararPopup = registerCoordinatePopup((coord) => {
    const { visivel, estilo } = useArStore.getState();
    const cams = camsDesenhado;
    if (!visivel || !estilo.modelo || !cams) return null;
    const [lon, lat] = mapToLonLat(coord[0], coord[1]);
    const i = celulaEm(cams, lon, lat, indiceCelulas);
    return i >= 0 ? renderCelula(i) : null;
  });

  // ---------------------------------------------------------------- assinaturas
  let listaDesenhada: readonly (Sensor | Estacao)[] = [];
  const pararStore = useArStore.subscribe((a, b) => {
    if (a.pontos !== b.pontos) {
      const nova = [...a.pontos.estacoes, ...a.pontos.sensores];
      const mesma =
        nova.length === listaDesenhada.length &&
        nova.every((p, k) => p.tipo === listaDesenhada[k]!.tipo && p.id === listaDesenhada[k]!.id);
      listaDesenhada = nova;
      if (!mesma) reconstruirPontos();
      else {
        // Mesma lista, dados novos: cada feature precisa do ponto NOVO (série
        // com o `t0` novo), senão a série velha é lida na grade nova — o mesmo
        // cuidado de `rivers/layer.ts`.
        const feats = fontePontos.getFeatures();
        const porChave = new Map(nova.map((p) => [`${p.tipo}|${p.id}`, p]));
        for (const f of feats) {
          const d = f.get('ar') as PontoFeature;
          const p = porChave.get(`${d.ponto.tipo}|${d.ponto.id}`);
          if (p) f.set('ar', { ponto: p, faixa: null } satisfies PontoFeature, true);
        }
        reavaliarPontos(true);
      }
    } else if (a.visivel !== b.visivel) reavaliarPontos(true);
    else if (a.estilo !== b.estilo) pontos.changed();

    if (a.cams !== b.cams) reconstruirModelo();
    else if (a.visivel !== b.visivel || a.estilo !== b.estilo) reavaliarModelo();
    if (a.celulaAberta !== b.celulaAberta || a.visivel !== b.visivel || a.estilo !== b.estilo) reavaliarDestaque();
  });

  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (shownTimeOf(a) !== shownTimeOf(b)) {
      reavaliarPontos();
      reavaliarModelo();
    }
  });

  listaDesenhada = [...useArStore.getState().pontos.estacoes, ...useArStore.getState().pontos.sensores];
  reconstruirPontos();
  reconstruirModelo();

  return {
    pontos,
    modelo,
    destaque,
    dispose: () => {
      pararStore();
      pararTempo();
      pararPopup();
      fontePontos.clear(true);
      fonteModelo.clear(true);
      fonteDestaque.clear(true);
    },
  };
}
